import axios from 'axios';
import {
  CG_VALIDATION_LIST,
  VERIFY_EMAIL_OTP,
  REGISTER_ENDPOINT,
} from '@/constants/registration-endpoints';
import { resolveCgId } from '@/utils/cg-status';

const INITIAL_STATE = {
  loading: false,
  currentStep: 0,
  registrationPageData: {
    selectedMemberStatus: null,
    selectedGroup: null,
    selectedLifeStage: null,
    aboutyourself: {},
    isOtpVerified: false,
  },
  cgData: [],
  countries: [],
  nationality: [],
};

const state = () => INITIAL_STATE;

const mutations = {
  setLoading(state, loading) {
    state.loading = loading;
  },
  setCurrentStep(state, step) {
    state.currentStep = step;
  },
  setRegistrationPageData(state, data) {
    state.registrationPageData = { ...state.registrationPageData, ...data };
  },
  setSelectedMemberStatus(state, status) {
    state.registrationPageData.selectedMemberStatus = status;
  },
  setSelectedGroup(state, group) {
    state.registrationPageData.selectedGroup = group;
  },
  setSelectedLifeStage(state, stage) {
    state.registrationPageData.selectedLifeStage = stage;
  },
  setAboutYourself(state, data) {
    state.registrationPageData.aboutyourself = {
      ...state.registrationPageData.aboutyourself,
      ...data,
    };
  },
  setOtpVerified(state, verified) {
    state.registrationPageData.isOtpVerified = verified;
  },
  setCgData(state, data) {
    state.cgData = data;
  },
  setCountries(state, data) {
    state.countries = data;
  },
  setNationality(state, data) {
    state.nationality = data;
  },
};

const actions = {
  async fetchCgData({ commit, dispatch }) {
    commit('setLoading', true);
    try {
      const { status, data } = await axios.get(process.env.VUE_APP_API_URL + CG_VALIDATION_LIST);
      if (status !== 200) throw new Error('Failed in getting CG');
      commit('setCgData', data);
    } catch (error) {
      console.error('Error fetching CG data:', error);
      dispatch('toast/error', 'Failed to load CG data', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchNationality({ commit, dispatch }) {
    commit('setLoading', true);
    try {
      const { status, data } = await axios.get(
        process.env.VUE_APP_API_URL + '/v2/profile/nationality',
      );
      if (status !== 200) throw new Error('Failed in getting nationality');
      commit('setNationality', data);
    } catch (error) {
      console.error('Error fetching nationality data:', error);
      dispatch('toast/error', 'Failed to load nationality data', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchCountries({ commit, dispatch }) {
    commit('setLoading', true);
    try {
      const { status, data } = await axios.get(
        process.env.VUE_APP_API_URL + '/v2/profile/countries',
      );
      if (status !== 200) throw new Error('Failed in getting countries');
      commit('setCountries', data);
    } catch (error) {
      console.error('Error fetching countries data:', error);
      dispatch('toast/error', 'Failed to load countries data', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },

  async sendEmailOtp({ state, commit, dispatch }, email) {
    commit('setLoading', true);
    try {
      const { status } = await axios.post(
        process.env.VUE_APP_API_URL + '/v2/profile/register/initiate-verify-email',
        { email: email || state.registrationPageData.aboutyourself.email },
      );
      if (status !== 200) throw new Error('Failed to send OTP');
      dispatch('toast/success', 'Email OTP sent successfully', { root: true });
    } catch (error) {
      dispatch('toast/error', error.response?.data?.message || 'Failed to send OTP', {
        root: true,
      });
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async verifyEmailOtp({ state, commit, dispatch }, { email, otp }) {
    commit('setLoading', true);
    try {
      const { status, data } = await axios.post(process.env.VUE_APP_API_URL + VERIFY_EMAIL_OTP, {
        email: email || state.registrationPageData.aboutyourself.email,
        otp: otp,
      });
      if (status !== 200) throw new Error('Failed to verify OTP');
      return { success: true, data };
    } catch (error) {
      dispatch('toast/error', error.response?.data?.message || 'Failed to verify OTP', {
        root: true,
      });
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async registerUser({ state, commit, dispatch }, password) {
    commit('setLoading', true);
    try {
      const { cgId, dateOfBirth, email, fullName, familyName, gender, preferredName, nationality } =
        state.registrationPageData.aboutyourself;

      // `cgId` holds the NAME the member typed (normalised on the way in). Resolve
      // it through the one shared lookup: stripping spaces from the input alone
      // never matched the directory's spaced keys ("TEENS 1"), so multi-word CGs
      // were silently dropped from the payload here.
      const cgIdValue = cgId ? resolveCgId(cgId, state.cgData) : undefined;

      const payLoad = {
        givenName: fullName,
        familyName: familyName,
        preferredName: preferredName,
        dateOfBirth: dateOfBirth,
        email: email,
        password: password,
        gender: gender.charAt(0).toUpperCase(),
        nationalityId: nationality,
      };

      // Only include cgId in payload if it has a valid value
      if (cgIdValue !== undefined) {
        payLoad.cgId = cgIdValue;
      }

      const { status, data } = await axios.post(
        process.env.VUE_APP_API_URL + REGISTER_ENDPOINT,
        payLoad,
      );
      if (status !== 200) throw new Error('Failed in registering user');

      return { success: true, data };
    } catch (error) {
      dispatch('toast/error', error.response?.data?.message || 'Failed to register user', {
        root: true,
      });
      console.error('Error registering user:', error);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  updateRegistrationData({ commit }, data) {
    commit('setRegistrationPageData', data);
  },

  updateMemberStatus({ commit }, status) {
    commit('setSelectedMemberStatus', status);
  },

  updateSelectedGroup({ commit }, group) {
    commit('setSelectedGroup', group);
  },

  updateLifeStage({ commit }, stage) {
    commit('setSelectedLifeStage', stage);
  },

  updateAboutYourself({ commit }, data) {
    commit('setAboutYourself', data);
  },

  setOtpVerificationStatus({ commit }, verified) {
    commit('setOtpVerified', verified);
  },

  updateStep({ commit }, step) {
    commit('setCurrentStep', step);
  },
};

const getters = {
  nationalityForSelect: state => {
    const sorted = state.nationality
      .map(nationality => ({
        value: nationality.id,
        label: nationality.name,
      }))
      .sort((a, b) => {
        // Check if label is "Singapore Citizen" (case-insensitive)
        const aIsSingapore = /^singaporean$/i.test(a.label);
        const bIsSingapore = /^singaporean$/i.test(b.label);

        if (aIsSingapore) return -1;
        if (bIsSingapore) return 1;
        return a.label.localeCompare(b.label);
      });
    return sorted;
  },
  countriesForSelect: state => {
    const sorted = state.countries
      .map(country => ({
        value: country.id,
        label: country.countryName,
      }))
      .sort((a, b) => {
        if (a.label === 'Singapore') return -1;
        if (b.label === 'Singapore') return 1;
        return a.label.localeCompare(b.label);
      });
    return sorted;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
